/**
 * «Resultat»-visning: enkel standardvisning for lokale classify-prosjekter (source = local-api).
 * Viser bare det vi lager: klasser (gruppert), funn (resultatlag med antall) og en kjøringsoppsummering,
 * pluss funn-stepperen (Forrige/Neste funn). Den fulle vieweren er uendret og ligger bak «Full visning».
 *
 * Logikken (gruppering, telling, funnliste, oppsummering, kort) er rene funksjoner og testes i
 * tests/unit/resultPanel.test.ts. DOM-delen får alt den trenger gjennom ResultHost.
 */

import { el } from '../format';
import { classInfo } from '../scene/classes';
import type { Feature } from '../layers/geojson';
import type { Picked, VectorLayer } from '../layers/overlays';
import {
  fallPriority, featureStyle, flag, GROUND_LAYER, GROUND_NOTE, groundSummary, isVegRiskLayer,
  OBJECT_CLEARANCE_LAYER, layerClassColor, supportCounts, UNCERTAIN_SUPPORTS_TOGGLE, VEG_NOTE, viewOptions,
  VOLTAGE_COLORS, WRITE_STYLE_NOTE, writtenFlag,
} from '../layers/featureStyle';
import { formatFields, VOLTAGE_LEVELS } from '../layers/fields';
import { FALL_LAYER, FALL_PART_LAYERS, fallCounts, fallCountText, normalizeFallProps } from '../layers/fallRisk';
import { STANDARD_PROFIL, type Leveranseprofil } from '../layers/leveranse';
import { customerBreakdown, customerCard, customerModel, voltageText, type CustomerRow, type Finding } from './customerView';
import { icon } from './icons';
import { densityText } from './projectMeta';
import { section } from './section';

export { voltageText };
export type { Finding };

// ------------------------------------------------------------------ klasser

export interface ClassGroup { id: string; title: string; codes: number[] }

/**
 * Geokontrolls leverte klassetabell (ASPRS + GK-klasser), i denne rekkefølgen. Alle klassene vises alltid, også med 0
 * punkter. Klasser i dataene som ikke står her, havner i «Andre» (vises bare når den har klasser).
 */
export const CLASS_GROUPS: ClassGroup[] = [
  { id: 'line', title: 'Linjeanlegg', codes: [15, 65, 64, 16, 14, 13] },
  { id: 'station', title: 'Stasjon', codes: [66, 67] },
  { id: 'terrain', title: 'Terreng', codes: [2] },
  { id: 'building', title: 'Bygning', codes: [6] },
  { id: 'vegetation', title: 'Vegetasjon', codes: [3, 4, 5] },
  { id: 'noise', title: 'Støy', codes: [7, 18] },
  { id: 'unclassified', title: 'Uklassifisert', codes: [1] },
  { id: 'other', title: 'Andre', codes: [] },
];
export const LINE_CLASSES = CLASS_GROUPS[0].codes;

export interface ClassRow {
  code: number; name: string; color: string; gk: boolean;
  /** lastede punkter (LOD) */
  count: number;
  /** netto endring i hele filen fra kjøringen (classify_manifest: klasseoverganger), om kjent */
  run?: number;
  /** ingen lastede punkter og ingen punkter lagt til av kjøringen: grå rad, bryteren er av */
  empty: boolean;
}
export interface GroupRow { group: ClassGroup; total: number; empty: boolean; classes: ClassRow[] }

/**
 * Netto endring per klasse i hele filen fra `classify.class_transitions` («1->15»: 9299): inn minus ut. Manifestet har
 * ikke totalt antall per klasse, så dette er bare det kjøringen endret (punkter som hadde klassen fra før er ikke med).
 */
export function runClassChange(raw: Record<string, unknown>): Map<number, number> | null {
  const c = raw.classify as Record<string, unknown> | undefined;
  const t = c && typeof c === 'object' ? c.class_transitions : undefined;
  if (!t || typeof t !== 'object') return null;
  const out = new Map<number, number>();
  for (const [k, v] of Object.entries(t as Record<string, unknown>)) {
    const m = /^\s*(\d+)\s*->\s*(\d+)\s*$/.exec(k);
    if (!m || typeof v !== 'number' || !Number.isFinite(v) || m[1] === m[2]) continue;
    out.set(Number(m[1]), (out.get(Number(m[1])) ?? 0) - v);
    out.set(Number(m[2]), (out.get(Number(m[2])) ?? 0) + v);
  }
  return out;
}

/** Lastede punkter per klasse → Geokontrolls klassetabell. Alle klassene i tabellen tas med; ukjente klasser med punkter havner i «Andre». */
export function groupClassCounts(counts: Map<number, number>, run: Map<number, number> | null = null): GroupRow[] {
  const known = new Set(CLASS_GROUPS.flatMap((g) => g.codes));
  const present = (c: number) => (counts.get(c) ?? 0) > 0 || (run?.get(c) ?? 0) > 0;
  const extra = [...new Set([...counts.keys(), ...(run?.keys() ?? [])])].filter((c) => !known.has(c) && present(c)).sort((a, b) => a - b);
  return CLASS_GROUPS.flatMap((group) => {
    const codes = group.id === 'other' ? extra : group.codes;
    if (!codes.length) return [];
    const classes = codes.map((code): ClassRow => {
      const info = classInfo(code);
      const r = run?.get(code);
      return { code, name: info.name, color: info.color, gk: !!info.gk, count: counts.get(code) ?? 0, ...(r ? { run: r } : {}), empty: !present(code) };
    });
    return [{ group: { ...group, codes }, total: classes.reduce((s, c) => s + c.count, 0), empty: classes.every((c) => c.empty), classes }];
  });
}

const signed = (n: number) => (n > 0 ? '+' : '−') + mill(Math.abs(n));
/** «1 801 (hele filen: +9 299)»; uten kjøringsdata bare lastede punkter. */
export const classCountText = (c: Pick<ClassRow, 'count' | 'run'>) => (c.run ? `${mill(c.count)} (hele filen: ${signed(c.run)})` : mill(c.count));
export const RUN_COUNT_NOTE = 'Antall = lastede punkter. «Hele filen» = endringen kjøringen gjorde i hele filen '
  + '(klasseoverganger i classify_manifest) – manifestet har ikke totalt antall per klasse, så punkter som hadde klassen fra før er ikke med.';
export const GK_TAG_TITLE = 'Geokontroll-definert klasse (ikke ASPRS-standard)';

/** Skjulte klasser etter at en gruppe slås av/på (alle klassene i gruppen følger hverandre). */
export function toggleGroup(hidden: Set<number>, codes: number[], show: boolean): Set<number> {
  const out = new Set(hidden);
  for (const c of codes) show ? out.delete(c) : out.add(c);
  return out;
}

/** «Vis bare linjeanlegg»: alt annet skjules. */
export const onlyLineHidden = () => new Set(Array.from({ length: 256 }, (_, i) => i).filter((i) => !LINE_CLASSES.includes(i)));

// ------------------------------------------------------------------ funn

/**
 * `swatch`: lag som tegnes i fargen til en punktklasse (se LAYER_CLASS i featureStyle.ts) får en fargeprikk.
 * `fallback`: reservelag (eldre kjøringer) – én liste, eller flere nivåer i prioritert rekkefølge.
 * `written`: antallet vises som «N klassifisert (+M usikre kandidater)».
 */
export interface FunnDef {
  id: string; title: string; layers: string[]; fallback?: string[] | string[][]; note?: string; legend?: 'voltage' | 'fall'; swatch?: string;
  written?: boolean;
  /** gruppe i panelet: avvik (det inspektøren leter etter) først, deretter anlegg og annet */
  group: FunnGroup;
}
export type FunnGroup = 'avvik' | 'anlegg' | 'annet';
export const FUNN_GROUPS: { id: FunnGroup; title: string }[] = [
  { id: 'avvik', title: 'Avvik' }, { id: 'anlegg', title: 'Anlegg' }, { id: 'annet', title: 'Annet' },
];

export const FALL_FUNN_LEGEND = 'Bue fra tretoppen ned til linja (rød = treffer linja, oransje = innenfor minsteavstanden), mørkerød strek = '
  + 'delen av treet over linja, ring = kontaktpunkt. Tykkere strek = høyere last-indeks (relativ andel av treets vekt – ikke en kraft).';

/** Rekkefølgen er prioriteten i panelet: avvik først (minsteavstand, fallrisiko), deretter anlegget og annet. */
export const FUNN: FunnDef[] = [
  { id: 'clearance', title: 'Minsteavstand', layers: ['veg_clearance_lines'], fallback: ['veg_clearance_violations'], group: 'avvik',
    note: 'Streker fra vegetasjon til linja med målt avstand. «kort» betyr at modellens krav ikke er oppfylt.' },
  { id: 'ground', title: 'Laveste høyde over bakken', layers: [GROUND_LAYER], group: 'avvik',
    note: 'Én loddrett linje per spenn (mast–mast): fra det laveste ledningspunktet ned til bakken. Rød = under kravet, grønn = OK, grå = krav ikke kontrollert. ' + GROUND_NOTE },
  { id: 'objects', title: 'Avstand til objekter under ledning', layers: [OBJECT_CLEARANCE_LAYER], group: 'avvik',
    note: 'Nærmeste objekt under ledningen per spenn og kategori, linje fra ledningen ned til toppen av objektet: oransje = bygg, grønn = vegetasjon, grå stiplet = annet. ' + GROUND_NOTE },
  // Ett logisk lag: bue fra tretoppen + mørkerød del over linja + kontaktpunkt (ring/etikett) per tre. Eldre fall-/faretre-lag vises ikke.
  { id: 'fall', title: 'Fallrisiko (trær mot linja)', layers: [FALL_LAYER], fallback: [[...FALL_PART_LAYERS]], legend: 'fall', group: 'avvik' },
  { id: 'supports', title: 'Master/stolper', layers: ['support_poles', 'support_pole_axes'], swatch: 'support_pole_axes', note: WRITE_STYLE_NOTE, written: true, group: 'anlegg' },
  { id: 'traverses', title: 'Traverser', layers: ['support_traverses'], swatch: 'support_traverses', written: true, group: 'anlegg' },
  { id: 'guys', title: 'Barduner', layers: ['support_guys'], swatch: 'support_guys', written: true, group: 'anlegg' },
  { id: 'insulators', title: 'Isolatorer', layers: ['insulators'], swatch: 'insulators', note: 'Kort strek fra festet på masten til ledningen.', group: 'anlegg' },
  { id: 'spans', title: 'Spenn (ledninger)', layers: ['conductor_wires'], legend: 'voltage', group: 'anlegg' },
  { id: 'noise', title: 'Støyklynger', layers: ['noise_clusters'], group: 'annet' },
  { id: 'buildings', title: 'Bygg', layers: ['buildings', 'veg_buildings', 'building_footprints'], group: 'annet' },
];

/** Antallsteksten delt i tall (høyre kolonne) og resten (linje under tittelen): «60 · 26 treffer linja» → «60» + «26 treffer linja». */
export function splitCount(text: string): { count: string; meta: string } {
  const m = /^(\d[\d\s ]*)(.*)$/.exec(text.trim());
  if (!m) return { count: text, meta: '' };
  return { count: m[1].trim(), meta: m[2].replace(/^\s*·\s*/, '').trim() };
}

export { VOLTAGE_COLORS };

/** Lagene en funnrad bruker: de primære som finnes i prosjektet, ellers reserven (eldre kjøringer). */
export function funnLayers(def: FunnDef, available: Set<string>): string[] {
  const primary = def.layers.filter((id) => available.has(id));
  if (primary.length) return primary;
  const fb = def.fallback ?? [];
  const tiers: string[][] = fb.length && Array.isArray(fb[0]) ? (fb as string[][]) : [fb as string[]];
  for (const t of tiers) {
    const hit = t.filter((id) => available.has(id));
    if (hit.length) return hit;
  }
  return [];
}

/**
 * Antallstekst for en funnrad. Bæreobjekter: «9 klassifisert (+43 usikre kandidater)» (uavhengig av om de usikre er
 * skjult). Fallrisiko: «N av M treffer» (M treffer linja, N oppfyller filteret). Ellers antall tegnede objekter i første lag.
 */
export function funnCountText(def: FunnDef, layerId: string, features: { id?: unknown; props: Record<string, unknown> }[]): string {
  if (def.written) {
    const c = supportCounts(layerId, features);
    return c.uncertain ? `${c.written} klassifisert (+${c.uncertain} usikre kandidater)` : `${c.written} klassifisert`;
  }
  if (layerId === GROUND_LAYER || layerId === OBJECT_CLEARANCE_LAYER) {
    const g = groundSummary(features, layerId === GROUND_LAYER ? 'min_hoyde_m' : 'avstand_m');
    const low = g.lowest === undefined ? '' : ` · ${layerId === GROUND_LAYER ? 'Lavest' : 'Nærmest'} ${g.lowest.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m`;
    return `${layerId === GROUND_LAYER ? g.spans : g.items}${low}${g.breaches ? ` · ${g.breaches} brudd` : ''}`;
  }
  if (layerId === FALL_LAYER || layerId === 'veg_fall_top_arcs' || layerId === 'veg_fall_contacts') {
    const c = fallCounts(features);
    return c ? fallCountText(c) : String(countShown(layerId, features));
  }
  return String(countShown(layerId, features));
}

/** Objekter som faktisk tegnes (skjulte – f.eks. forkastede eller fall uten risiko – telles ikke). */
export function countShown(layerId: string, features: { id?: unknown; props: Record<string, unknown> }[]): number {
  const shown = features.filter((f) => !featureStyle(layerId, f.props)?.hidden);
  // multigeometrier deles i flere deler med samme ID ved parsing: tell objekter, ikke deler
  return shown.every((f) => f.id !== undefined && f.id !== null && f.id !== '') ? new Set(shown.map((f) => String(f.id))).size : shown.length;
}

// ------------------------------------------------------------------ neste/forrige funn

/**
 * Funn å gå gjennom, i prioritert rekkefølge: brudd på minsteavstand → fallrisiko → usikre bæreobjekter (bare når de
 * vises). Fallrisiko fra toppen sorteres etter lengde over linja (størst først), deretter nesten-treff (minst mangel).
 */
export function collectFindings(get: (id: string) => Feature[] | undefined): Finding[] {
  const out: Finding[] = [];
  const add = (layerId: string, kind: string, keep: (p: Record<string, unknown>) => boolean) => {
    for (const f of get(layerId) ?? []) if (keep(f.props)) out.push({ layerId, feature: f, kind });
  };
  const num = (v: unknown) => (typeof v === 'number' ? v : Number(v));
  if (get('veg_clearance_lines')) add('veg_clearance_lines', 'Minsteavstand', (p) => num(p.deficit_m) > 0);
  else add('veg_clearance_violations', 'Minsteavstand', () => true);
  // bakkeavstand: brudd på krav (lavest først), deretter objekter under ledningen
  for (const [id, kind, key] of [[GROUND_LAYER, 'Lav høyde over bakken', 'min_hoyde_m'], [OBJECT_CLEARANCE_LAYER, 'Kort avstand til objekt', 'avstand_m']] as const) {
    const fs = (get(id) ?? []).filter((f) => num(f.props.brudd) === 1 && !featureStyle(id, f.props)?.hidden)
      .map((f, i) => ({ f, i, v: num(f.props[key]) })).sort((a, b) => (a.v || 0) - (b.v || 0) || a.i - b.i);
    for (const { f } of fs) out.push({ layerId: id, feature: f, kind: `${kind} · ${featureStyle(id, f.props)?.label ?? ''}`.replace(/ · $/, '') });
  }
  const single = get(FALL_LAYER);
  const contacts = single ? undefined : get('veg_fall_contacts');
  if (single) {
    const shown = single.filter((f) => !featureStyle(FALL_LAYER, f.props)?.hidden)
      .map((f, i) => ({ f, i, pr: fallPriority(normalizeFallProps(f.props)) }))
      .sort((a, b) => b.pr - a.pr || a.i - b.i);
    for (const { f } of shown) {
      const lab = featureStyle(FALL_LAYER, f.props)?.label;
      out.push({ layerId: FALL_LAYER, feature: f, kind: lab ? `Fallrisiko · ${lab}` : 'Fallrisiko' });
    }
  } else if (contacts) {
    const shown = contacts.filter((f) => !featureStyle('veg_fall_contacts', f.props)?.hidden)
      .map((f, i) => ({ f, i, pr: fallPriority(f.props) }))
      .sort((a, b) => b.pr - a.pr || a.i - b.i);
    for (const { f } of shown) {
      const lab = featureStyle('veg_fall_contacts', f.props)?.label;
      out.push({ layerId: 'veg_fall_contacts', feature: f, kind: lab ? `Fallrisiko · ${lab}` : 'Fallrisiko' });
    }
  } else if (get('veg_fall_lines')) add('veg_fall_lines', 'Fallrisiko', (p) => !featureStyle('veg_fall_lines', p)?.hidden);
  else add('veg_danger_trees', 'Fallrisiko', (p) => flag(p.fall_in) !== false);
  add('support_poles', 'Usikker stolpe', (p) => !!featureStyle('support_poles', p)?.dashed);
  return out;
}

/** Neste/forrige indeks med omløp; fra «ingen valgt» (-1) går forrige til siste. */
export const stepIndex = (i: number, step: number, n: number) => (n ? ((((i < 0 && step < 0 ? 0 : i) + step) % n) + n) % n : -1);

const KIND_PLURAL: Record<string, [string, string]> = {
  Minsteavstand: ['brudd på minsteavstand', 'brudd på minsteavstand'], Fallrisiko: ['fallrisiko', 'fallrisiko'],
  'Lav høyde over bakken': ['lav høyde over bakken', 'lave høyder over bakken'], 'Kort avstand til objekt': ['kort avstand til objekt', 'korte avstander til objekter'], 'Usikker stolpe': ['usikker stolpe', 'usikre stolper'],
};
/** Fordeling av funnene per type, i prioritert rekkefølge: «18 brudd på minsteavstand», «26 fallrisiko». */
export function findingBreakdown(findings: Pick<Finding, 'kind'>[]): { kind: string; count: number; text: string }[] {
  const counts = new Map<string, number>();
  for (const f of findings) { const k = f.kind.split(' · ')[0]; counts.set(k, (counts.get(k) ?? 0) + 1); }
  return [...counts].map(([kind, count]) => {
    const [one, many] = KIND_PLURAL[kind] ?? [kind.toLowerCase(), kind.toLowerCase()];
    return { kind, count, text: `${count.toLocaleString('nb-NO')} ${count === 1 ? one : many}` };
  });
}

/** Stepperens posisjonstekst: «3 av 61», eller «61 funn» før noe er valgt. */
export function stepperLabel(index: number, total: number): string {
  if (!total) return 'Ingen funn';
  return index < 0 ? `${total.toLocaleString('nb-NO')} funn` : `${(index + 1).toLocaleString('nb-NO')} av ${total.toLocaleString('nb-NO')}`;
}

/** Funnets plass i gjennomgangen (vises også i høyrepanelet). */
export interface FindingContext { index: number; total: number; kind: string }

// ------------------------------------------------------------------ oppsummering

const SOURCE_NAMES: Record<string, string> = {
  SUPPORT: 'Bæreobjekter', GROUND_REPAIR: 'Bakkereparasjon', CONDUCTORS: 'Ledere', VEGETATION: 'Vegetasjon', NOISE: 'Støy', INSULATORS: 'Isolatorer',
};
const nb = (n: number) => n.toLocaleString('nb-NO');
export const mill = (n: number) => (n >= 1e6 ? `${(n / 1e6).toLocaleString('nb-NO', { maximumFractionDigits: 1 })} mill.` : nb(n));

export function runSummary(raw: Record<string, unknown>): { lines: string[]; uncalibrated: boolean } | null {
  const c = raw.classify as Record<string, unknown> | undefined;
  if (!c || typeof c !== 'object') return null;
  if (!c.available) return { lines: ['Kjøringen er ikke ferdig ennå. Viser eldre resultater og ferdige steg.'], uncalibrated: false };
  const lines: string[] = [];
  const t = typeof c.created_utc === 'string' ? new Date(c.created_utc) : null;
  const when = t && Number.isFinite(t.getTime()) ? t.toLocaleString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;
  lines.push([c.name, when].filter(Boolean).join(' · '));
  if (typeof c.points === 'number') lines.push(`${mill(c.points)} punkter` + (typeof c.class_changed === 'number' ? ` · ${mill(c.class_changed)} fikk ny klasse` : ''));
  const by = c.class_changed_by_source as Record<string, number> | undefined;
  if (by && typeof by === 'object') {
    const parts = Object.entries(by).filter(([, v]) => typeof v === 'number').sort((a, b) => b[1] - a[1]).map(([k, v]) => `${SOURCE_NAMES[k] ?? k} ${mill(v)}`);
    if (parts.length) lines.push('Endret: ' + parts.join(' · '));
  }
  const dens = densityText(raw);
  if (dens) lines.push(dens);
  const lp = raw.leveranseprofil as { kilde?: string; fil?: string | null; kunde?: string | null } | undefined;
  if (lp && typeof lp === 'object') {
    lines.push(lp.kilde === 'standard' || !lp.fil ? 'Funn for kunden: standardinnstillinger (ingen leveranseprofil)'
      : `Funn for kunden: ${lp.kunde ? `${lp.kunde}, ` : ''}${lp.fil}`);
  }
  return { lines, uncalibrated: String(c.calibration_status ?? '').toUpperCase() === 'UNCALIBRATED' };
}

// ------------------------------------------------------------------ infokort

const TYPE_NAMES: Record<string, string> = {
  support_poles: 'Stolpe', support_pole_axes: 'Stolpeakse', support_traverses: 'Travers', support_guys: 'Bardun', insulators: 'Isolator',
  conductor_wires: 'Ledning', conductor_spans: 'Spenn', veg_fall_lines: 'Fallrisiko (tre)', veg_fall_impacts: 'Treffpunkt på linjen',
  // fallrisiko er ett logisk lag: samme kort uansett hvilken del (bue, del over linja, kontaktpunkt) som klikkes
  [FALL_LAYER]: 'Fallrisiko – tre mot linja', veg_fall_top_arcs: 'Fallrisiko – tre mot linja', veg_fall_overshoot: 'Fallrisiko – tre mot linja', veg_fall_contacts: 'Fallrisiko – tre mot linja',
  veg_fall_arcs: 'Fallbue', veg_clearance_lines: 'Minsteavstand', veg_danger_trees: 'Faretre', veg_clearance_violations: 'Brudd på minsteavstand',
  noise_clusters: 'Støyklynge', noise_cluster_points: 'Støyklynge',
  [GROUND_LAYER]: 'Laveste høyde over bakken', [OBJECT_CLEARANCE_LAYER]: 'Avstand til objekt under ledning',
};

const BRUDD_TEXT: Record<string, string> = { '1': 'Brudd – under kravet', '0': 'OK – innenfor kravet', '-1': 'Ikke kontrollert (krav mangler)' };
const BAKKEKILDE: Record<string, string> = { punkter: 'bakkepunkter (klasse 2)', dtm: 'terrengmodell (DTM)', blandet: 'bakkepunkter og DTM' };
/** Infokort for bakkeavstand / objektavstand (norske felt fra motoren). */
function groundRows(layerId: string, props: Record<string, unknown>, rows: [string, string][]): string {
  const m = (k: string, d = 1) => { const v = numv(props[k]); return v === undefined ? undefined : `${nbn(v, d)} m`; };
  const push = (k: string, v: string | undefined) => { if (v !== undefined && v !== '') rows.push([k, v]); };
  const s = (k: string) => (typeof props[k] === 'string' && (props[k] as string).trim() ? (props[k] as string).trim() : undefined);
  if (layerId === GROUND_LAYER) push('Laveste høyde over bakken', m('min_hoyde_m', 2));
  else {
    const kat = s('kategori');
    push('Objekt', kat ? kat.charAt(0).toUpperCase() + kat.slice(1) : undefined);
    const kl = numv(props.objektklasse);
    if (kl !== undefined) push('Punktklasse', `${kl} ${classInfo(kl).name}`);
    push('Avstand ledning → objekt (loddrett)', m('avstand_m', 2));
    push('Horisontal forskyvning', m('horisontal_m', 2));
    push('Objektets høyde over bakken', m('objekt_over_bakke_m'));
  }
  const b = props.brudd === undefined || props.brudd === null ? undefined : String(numv(props.brudd));
  push('Vurdering', b !== undefined ? BRUDD_TEXT[b] ?? b : undefined);
  push('Krav (modell)', m('krav_m') ?? 'ikke satt');
  push('Margin', m('margin_m', 2));
  push('Spenn', s('span_id'));
  push('Spennlengde', m('spennlengde_m'));
  const kj = numv(props.kjede_m);
  if (kj !== undefined) push('Plass i spennet', `${nbn(kj, 1)} m fra spennstart`);
  push('Ledning', [s('wire_id'), s('rolle')].filter(Boolean).join(' · ') || undefined);
  push('Spenningsnivå', s('spenningsniva'));
  if (flag(props.styrende) !== undefined) push('Styrende (laveste) leder i spennet', flag(props.styrende) ? 'ja' : 'nei');
  const kilde = s('bakkekilde');
  if (kilde) push('Bakkehøyde fra', BAKKEKILDE[kilde.toLowerCase()] ?? kilde);
  const ls = s('leder_status');
  if (ls) push('Lederstatus', STATUS_NAMES[ls.toUpperCase()] ?? ls);
  const merk = s('merknad');
  const cap = merk ? merk.charAt(0).toUpperCase() + merk.slice(1) + (merk.endsWith('.') ? '' : '.') : '';
  return [cap, /UNCALIBRATED/i.test(merk ?? '') ? '' : GROUND_NOTE].filter(Boolean).join(' ');
}
const STATUS_NAMES: Record<string, string> = { VALIDATED: 'Validert', UNCERTAIN: 'Usikker', REJECTED: 'Forkastet', DELIVERED: 'Levert (analyse)', REVIEW: 'Til kontroll' };
/** Felt som vises på kortet, i denne rekkefølgen (verdiene formateres som i full objektinfo). */
const CARD_FIELDS = ['support_type', 'role', 'confidence', 'height_m', 'tilt_deg', 'length_m', 'dist_3d_m', 'dist_h_m', 'min_d3_m',
  'dist_top_3d_m', 'required_clearance_m', 'clearance_m', 'threshold_m', 'deficit_m', 'margin_m', 'fall_in_margin_m', 'sag_m', 'attach_agl_m'];

/** Isolatortype fra classify (`type`) i klartekst. */
export const INSULATOR_TYPES: Record<string, string> = { PIN_POST: 'stående isolator', SUSPENSION: 'hengende kjede', STRAIN: 'avspenningskjede' };
const INSULATOR_REASONS: Record<string, string> = {
  WIRE_NOT_ENDING_AT_SUPPORT: 'ledningen ender ikke ved masten', IN_CLUTTER: 'mange punkter rundt (vegetasjon/konstruksjon)',
};

/** Isolator: type, status, punktsky, lengde (cm-oppløsning: kjedene er under 1 m), bæreobjekt, konfidens og spenningshint. */
function insulatorRows(props: Record<string, unknown>, rows: [string, string][]): void {
  const t = typeof props.type === 'string' ? props.type.trim().toUpperCase() : '';
  rows[0] = ['Type', t ? `Isolator – ${INSULATOR_TYPES[t] ?? t}` : 'Isolator'];
  const len = typeof props.length_m === 'number' ? props.length_m : Number(props.length_m);
  if (props.length_m !== undefined && props.length_m !== null && Number.isFinite(len)) rows.push(['Lengde', `${len.toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m`]);
  if (typeof props.support_id === 'string' && props.support_id) rows.push(['Bæreobjekt', props.support_id]);
  const fields = new Map(formatFields(props).map((r) => [r.key, r]));
  const c = fields.get('confidence');
  if (c) rows.push([c.label, c.value]);
  const hint = typeof props.voltage_hint_name === 'string' ? props.voltage_hint_name.trim() : '';
  if (hint && hint.toUpperCase() !== 'UNKNOWN') rows.push(['Spenning (anslag fra isolator)', hint]);
  const reasons = typeof props.review_reasons === 'string' ? props.review_reasons.split(/[;,]/).map((r) => r.trim()).filter(Boolean) : [];
  if (reasons.length) rows.push(['Til kontroll', reasons.map((r) => INSULATOR_REASONS[r] ?? r).join(', ')]);
}

const FALL_RISK_IDS = new Set<string>([FALL_LAYER, ...FALL_PART_LAYERS]);

/**
 * Fallrisiko er ett logisk lag: et klikk på buen eller delen over linja viser treets kort fra kontaktpunktet (rikest
 * attributter), funnet via `tree_id`. Returnerer indeksen i `contacts`, eller -1 når laget/treet ikke har kontaktpunkt.
 */
export function fallContactIndex(layerId: string, props: Record<string, unknown>, contacts: { props: Record<string, unknown> }[] | undefined): number {
  if (!FALL_RISK_IDS.has(layerId) || layerId === 'veg_fall_contacts' || !contacts) return -1;
  const id = props.tree_id;
  if (id === undefined || id === null || id === '') return -1;
  return contacts.findIndex((c) => c.props.tree_id !== undefined && c.props.tree_id !== null && String(c.props.tree_id) === String(id));
}
export const LOAD_INDEX_NOTE = 'Last-indeksen er relativ (andel av treets egen vekt som hviler på linja i en enkel momentmodell) – ikke en kraft. '
  + 'Reell last avhenger av treslag, stammediameter, kronemasse, vind, nedheng og strekk, som ikke er målt.';
const nbn = (v: number, d: number) => v.toLocaleString('nb-NO', { minimumFractionDigits: d, maximumFractionDigits: d });
const numv = (v: unknown): number | undefined => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : undefined;
};

/** Infokort for fallrisiko fra toppen: høyde, avstand, hvor på stammen, lengde over linja, last-indeks, spenning. */
function fallRiskRows(props: Record<string, unknown>, rows: [string, string][]): void {
  const geo = !!flag(props.hits_geometric), hits = !!flag(props.hits);
  const m = (k: string) => { const v = numv(props[k]); return v === undefined ? undefined : `${nbn(v, 1)} m`; };
  const pct = (k: string) => { const v = numv(props[k]); return v === undefined ? undefined : `${nbn(v * 100, 0)} %`; };
  const push = (k: string, v: string | undefined) => { if (v !== undefined) rows.push([k, v]); };
  if (props.tree_id !== undefined && props.tree_id !== null) rows.push(['Tre-ID', String(props.tree_id)]);
  rows.push(['Vurdering', geo ? 'treffer linja ved fall' : hits ? 'kommer innenfor minsteavstanden, men treffer ikke linja' : 'når ikke linja']);
  push('Trehøyde', m('height_m'));
  push('Avstand stammefot → linja (3D)', m('dist_3d_m'));
  if (geo) {
    const cf = pct('contact_fraction');
    if (cf) rows.push(['Treffer linja ved', `${cf} av stammen (fra rot)`]);
    const ov = m('overshoot_m'), of = pct('overshoot_fraction');
    if (ov) rows.push(['Lengde over linja', of ? `${ov} (${of} av treet)` : ov]);
    push('Krone over linja (antatt)', m('crown_over_line_m'));
    const li = numv(props.load_index);
    if (li !== undefined) rows.push(['Last-indeks (relativ, ikke kraft)', nbn(li, 2)]);
  } else push('Mangler til linja', m('shortfall_m'));
  push('Minsteavstand (modell, ikke forskrift)', m('clearance_m'));
  if (typeof props.wire_id === 'string' && props.wire_id) rows.push(['Ledning', props.wire_id]);
}

export function cardRows(layerId: string, title: string, props: Record<string, unknown>): { rows: [string, string][]; note?: string } {
  const rows: [string, string][] = [['Type', TYPE_NAMES[layerId] ?? title]];
  if (layerId === GROUND_LAYER || layerId === OBJECT_CLEARANCE_LAYER) return { rows, note: groundRows(layerId, props, rows) };
  if (FALL_RISK_IDS.has(layerId)) {
    const p = normalizeFallProps(props);
    fallRiskRows(p, rows);
    const v = voltageText(p);
    if (v) rows.push(['Spenningsnivå', v]);
    return { rows, note: `${VEG_NOTE} ${LOAD_INDEX_NOTE}` };
  }
  const st = typeof props.status === 'string' ? props.status.toUpperCase() : undefined;
  const written = writtenFlag(props);
  if (st) rows.push(['Status', STATUS_NAMES[st] ?? st]);
  if (written !== undefined) rows.push(['I punktskyen', written ? 'ja (klassifisert)' : 'nei']);
  if (layerId === 'insulators') {
    insulatorRows(props, rows);
    const uncal = String(props.calibration_status ?? '').toUpperCase() === 'UNCALIBRATED';
    return { rows, note: uncal ? 'UNCALIBRATED – tersklene er ikke kalibrert; ingen nøyaktighet er påstått.' : undefined };
  }
  const fields = new Map(formatFields(props).map((r) => [r.key, r]));
  for (const k of CARD_FIELDS) {
    const r = fields.get(k);
    if (r) rows.push([r.label, r.value]);
  }
  const v = voltageText(props);
  if (v) rows.push(['Spenningsnivå', v]);
  const note = isVegRiskLayer(layerId) || /^veg_(danger|clearance)/.test(layerId) ? VEG_NOTE
    : String(props.calibration_status ?? '').toUpperCase() === 'UNCALIBRATED' ? 'UNCALIBRATED – tersklene er ikke kalibrert; ingen nøyaktighet er påstått.' : undefined;
  return { rows, note };
}

// ------------------------------------------------------------------ DOM

export interface ResultHost {
  raw: Record<string, unknown>;
  classCounts: () => Map<number, number>;
  hiddenClasses: () => Set<number>;
  setHiddenClasses: (s: Set<number>) => void;
  layers: () => VectorLayer[];
  setVisible: (id: string, v: boolean) => void;
  load: (l: VectorLayer) => void;
  /** fly til objektet og velg det; `ctx` er satt når valget kommer fra funn-stepperen */
  goTo: (p: Picked, ctx?: FindingContext) => void;
  showFull: () => void;
  /** «Vis usikre mastkandidater (ikke klassifisert)» – av som standard i Resultat-visningen */
  showUncertainSupports?: () => boolean;
  setShowUncertainSupports?: (v: boolean) => void;
  /** «Tilbakestill visning»: tilbake til ren standard (bare punktskyen) */
  resetView?: () => void;
  /** egen gruppe nederst i Funn (f.eks. «Bilder» → Dronebilder); samme element hver gang */
  extraGroup?: () => HTMLElement | null;
  /** kundens leveranseprofil (prosjekt-JSON `leveranseprofil`); standard når den mangler */
  profil?: Leveranseprofil;
  /** «Som kunden ser det» (true) / «Vis alt» (false) er byttet: tegningen må følge (setLeveranseView) */
  onAudience?: (customer: boolean) => void;
}

export type Audience = 'kunde' | 'alt';
const AUDIENCE_KEY = 'gk-resultat-visning';
function readAudience(): Audience {
  try { return localStorage.getItem(AUDIENCE_KEY) === 'alt' ? 'alt' : 'kunde'; } catch { return 'kunde'; }
}
function storeAudience(a: Audience): void {
  try { localStorage.setItem(AUDIENCE_KEY, a); } catch { /* privat modus: valget gjelder bare denne økten */ }
}

export interface ResultPanel {
  root: HTMLElement;
  update: () => void;
  /** neste (+1) / forrige (−1) funn; false når det ikke finnes funn ennå */
  step: (dir: number) => boolean;
  /** funnet som er valgt nå (null før stepperen er brukt) */
  current: () => (FindingContext & { layerId: string; feature: Feature }) | null;
  /** start gjennomgangen på nytt (etter «Tilbakestill visning») */
  reset: () => void;
  /** «Som kunden ser det» er valgt (standard) */
  customer: () => boolean;
}

/**
 * Lagene som slås på når et funn vises: hele det logiske laget funnet hører til (f.eks. fallrisiko = bue + del over linja +
 * kontaktpunkt), ellers bare funnets eget lag.
 */
export function layersForFinding(layerId: string, available: Set<string>): string[] {
  const def = FUNN.find((d) => funnLayers(d, available).includes(layerId));
  return def ? funnLayers(def, available) : [layerId];
}

const skeleton = (w = '2.5em') => el('span', { class: 'skel', style: `width:${w}`, 'aria-hidden': 'true' });

/** Liten «i»-knapp som viser/skjuler en forklaring under raden. */
function infoToggle(label: string, note: HTMLElement): HTMLButtonElement {
  note.classList.add('hidden');
  const b = el('button', { type: 'button', class: 'btn-icon btn-xs res-info', 'aria-label': `Forklaring: ${label}`, 'aria-expanded': 'false', 'data-tip': 'Forklaring', 'data-tip-pos': 'left',
    onclick: () => { const open = note.classList.toggle('hidden') === false; b.setAttribute('aria-expanded', String(open)); b.classList.toggle('active', open); } }, icon('info')) as HTMLButtonElement;
  return b;
}

export function resultPanel(host: ResultHost): ResultPanel {
  const classesEl = el('div', { class: 'res-classes' });
  const groupsEl = el('div', { class: 'res-funn' });
  // ---- stepper
  const pos = el('b', { class: 'stepper-pos mono', 'aria-live': 'polite' }, skeleton('4em'));
  const kind = el('span', { class: 'stepper-kind' }, 'Laster funn …');
  const breakdown = el('div', { class: 'stepper-break' });
  const prevBtn = el('button', { type: 'button', class: 'btn btn-icon-only', 'aria-label': 'Forrige funn (P)', 'data-tip': 'Forrige funn · P', onclick: () => step(-1) }, icon('prev')) as HTMLButtonElement;
  const nextBtn = el('button', { type: 'button', class: 'btn btn-primary stepper-next', 'aria-label': 'Neste funn (N)', 'data-tip': 'Neste funn · N', onclick: () => step(1) }, 'Neste funn', icon('next')) as HTMLButtonElement;
  const stepper = el('div', { class: 'stepper', role: 'group', 'aria-label': 'Gå gjennom funn' },
    el('div', { class: 'stepper-main' }, prevBtn, el('div', { class: 'stepper-text' }, pos, kind), nextBtn),
    breakdown);

  let findings: Finding[] = [];
  let index = -1;
  let loading = true;
  const profil = host.profil ?? { ...STANDARD_PROFIL };
  let audience: Audience = readAudience();
  const renderStepper = () => {
    const n = findings.length;
    prevBtn.disabled = nextBtn.disabled = !n;
    if (!n) {
      pos.replaceChildren(loading ? skeleton('4em') : 'Ingen funn');
      kind.textContent = loading ? 'Laster analyselag …' : audience === 'kunde' ? 'Ingen funn som må følges opp.'
        : 'Ingen brudd på minsteavstand, fallrisiko eller usikre master i lastede lag.';
      breakdown.replaceChildren();
      return;
    }
    pos.textContent = stepperLabel(index, n);
    kind.textContent = index < 0 ? 'Start med det viktigste' : findings[index].kind;
    breakdown.replaceChildren(...(audience === 'kunde'
      ? customerBreakdown(findings).map((b) => el('span', { class: `stepper-chip k-${b.severity === 'oppfolging' ? 'danger' : 'info'}` }, b.text))
      : findingBreakdown(findings).map((b) => el('span', { class: `stepper-chip k-${b.kind === 'Usikker stolpe' ? 'warn' : 'danger'}` }, b.text))));
  };
  const step = (dir: number): boolean => {
    if (!findings.length) return false;
    index = stepIndex(index, dir, findings.length);
    const f = findings[index];
    const all = host.layers().filter((l) => l.def.inProject);
    const layer = all.find((l) => l.def.id === f.layerId);
    if (!layer) return false;
    // slå på hele det logiske laget funnet hører til (og la det stå på til brukeren slår det av)
    for (const id of layersForFinding(f.layerId, new Set(all.map((l) => l.def.id)))) {
      const l = all.find((x) => x.def.id === id);
      if (l && !l.visible) host.setVisible(id, true);
    }
    renderStepper();
    // kundevisningen: kortet har allerede setningen, så funnlinja viser bare alvorsgraden
    const ctxKind = f.severity ? (f.severity === 'oppfolging' ? 'Må følges opp' : 'Til orientering') : f.kind;
    host.goTo({ layer, feature: f.feature, at: f.feature.parts[0][0] as [number, number, number?] }, { index, total: findings.length, kind: ctxKind });
    return true;
  };

  // ---- klasser og kjøring (sammenleggbare)
  const summary = runSummary(host.raw);
  const run = runClassChange(host.raw);
  const classTools = el('div', { class: 'res-class-tools' },
    el('button', { type: 'button', class: 'btn btn-sm', onclick: () => host.setHiddenClasses(onlyLineHidden()) }, 'Bare linjeanlegg'),
    el('button', { type: 'button', class: 'btn btn-sm', onclick: () => host.setHiddenClasses(new Set()) }, 'Vis alle'));
  const classSection = section('res-classes', 'Klasser', el('div', {}, classTools, classesEl,
    ...(run ? [el('p', { class: 'note' }, RUN_COUNT_NOTE)] : [])), { open: true });
  const runSection = summary ? section('res-run', 'Om kjøringen', el('div', { class: 'res-summary' },
    ...summary.lines.map((t) => el('p', {}, t)),
    summary.uncalibrated ? el('p', { class: 'note warn' }, 'Ukalibrert (UNCALIBRATED): ingen nøyaktighet er påstått.') : null), { open: false }) : null;

  const audBtns = (['kunde', 'alt'] as const).map((a) => el('button', { type: 'button', class: audience === a ? 'seg-btn active' : 'seg-btn', 'aria-pressed': String(audience === a),
    'data-tip': a === 'kunde' ? 'Bare funnene kundens leveranseprofil tar med' : 'Alle analyselag, også usikre kandidater', 'data-tip-pos': 'bottom',
    onclick: () => setAudience(a) }, a === 'kunde' ? 'Som kunden ser det' : 'Vis alt') as HTMLButtonElement);
  const audienceSeg = el('div', { class: 'seg res-audience', role: 'group', 'aria-label': 'Visning av funn' }, ...audBtns);
  const setAudience = (a: Audience) => {
    if (a === audience) return;
    audience = a;
    storeAudience(a);
    audBtns.forEach((b, i) => { const on = (i === 0 ? 'kunde' : 'alt') === a; b.setAttribute('aria-pressed', String(on)); b.classList.toggle('active', on); });
    index = -1;
    key = '';
    host.onAudience?.(a === 'kunde');
    update();
  };
  const root = el('section', { class: 'result-panel' },
    el('div', { class: 'res-block' }, el('div', { class: 'panel-heading' }, el('h2', {}, 'Funn'),
      host.resetView ? el('button', { type: 'button', class: 'btn btn-ghost btn-sm', 'data-tip': 'Skjul alle lag og vis bare punktskyen', 'data-tip-pos': 'left', onclick: () => host.resetView!() }, icon('reset'), 'Tilbakestill visning') : null),
      audienceSeg, stepper, groupsEl),
    classSection.root, runSection?.root ?? null);

  // Last alle funnlag (små GeoPackager, delt nedlasting) så antallene er kjent – synlighet endres ikke.
  let loadedOnce = false;
  let key = '';
  const update = () => {
    const layers = host.layers().filter((l) => l.def.inProject);
    if (!loadedOnce && layers.length) {
      loadedOnce = true;
      const ids = new Set(FUNN.flatMap((d) => funnLayers(d, new Set(layers.map((l) => l.def.id)))));
      for (const l of layers) if (ids.has(l.def.id) && l.status === 'idle') host.load(l);
    }
    const counts = host.classCounts();
    const hidden = host.hiddenClasses();
    const byId = new Map(layers.map((l) => [l.def.id, l]));
    const k = JSON.stringify([audience, [...counts], [...hidden], viewOptions.hideUnwrittenSupports, layers.map((l) => [l.def.id, l.status, l.visible, l.parsed?.features.length])]);
    if (k === key) return;
    key = k;
    const prev = index >= 0 ? findings[index] : null;
    const getReady = (id: string) => { const l = byId.get(id); return l?.status === 'ready' ? l.parsed?.features : undefined; };
    const model = audience === 'kunde' ? customerModel(getReady, new Set(byId.keys()), profil) : null;
    findings = model ? model.findings : collectFindings(getReady);
    // behold plassen i gjennomgangen når lister bygges på nytt (f.eks. et lag lastes ferdig)
    index = prev ? findings.findIndex((f) => f.layerId === prev.layerId && f.feature === prev.feature) : -1;
    const funnIds = new Set(FUNN.flatMap((d) => funnLayers(d, new Set(byId.keys()))));
    loading = [...funnIds].some((id) => ['idle', 'loading'].includes(byId.get(id)?.status ?? ''));
    renderStepper();

    if (model) renderCustomer(model, byId);
    else groupsEl.replaceChildren(...FUNN_GROUPS.flatMap((g) => {
      const defs = FUNN.filter((d) => d.group === g.id && funnLayers(d, new Set(byId.keys())).length);
      if (!defs.length) return [];
      return [el('div', { class: `res-group res-group-${g.id}`, role: 'group', 'aria-label': g.title },
        el('h3', { class: 'res-group-title' }, g.title),
        ...defs.flatMap((def) => funnRow(def, byId)))];
    }));
    if (!groupsEl.childElementCount) groupsEl.append(el('p', { class: 'empty' }, 'Prosjektet har ingen analyselag ennå.'));
    const extra = host.extraGroup?.();
    if (extra) groupsEl.append(extra);

    const groups = groupClassCounts(counts, run);
    classesEl.replaceChildren(
      ...(groups.some((g) => g.total) ? [] : [el('p', { class: 'empty' }, 'Ingen punkter lastet ennå. Antallene fylles inn mens punktskyen lastes.')]),
      ...groups.map((g) => {
        const live = g.classes.filter((c) => !c.empty);
        const gbox = el('input', { type: 'checkbox', checked: !g.empty && live.every((c) => !hidden.has(c.code)), disabled: g.empty,
          'aria-label': `Vis ${g.group.title}`,
          onchange: () => host.setHiddenClasses(toggleGroup(host.hiddenClasses(), g.group.codes, gbox.checked)) }) as HTMLInputElement;
        return el('div', { class: g.empty ? 'res-cgroup res-empty' : 'res-cgroup' },
          el('label', { class: 'res-crow res-cgroup-head' }, gbox, el('b', {}, g.group.title), el('span', { class: 'res-num mono' }, mill(g.total))),
          ...g.classes.map((c) => {
            const cb = el('input', { type: 'checkbox', checked: !c.empty && !hidden.has(c.code), disabled: c.empty, 'aria-label': `Vis klasse ${c.code} ${c.name}`,
              onchange: () => host.setHiddenClasses(toggleGroup(host.hiddenClasses(), [c.code], cb.checked)) }) as HTMLInputElement;
            return el('label', { class: c.empty ? 'res-crow res-class res-empty' : 'res-crow res-class', title: c.empty ? 'Ingen punkter i denne klassen' : '' }, cb,
              el('i', { class: 'dot', style: `background:${c.color}` }),
              el('span', { class: 'res-cname' }, el('span', { class: 'res-code mono' }, String(c.code)), el('span', { title: c.name }, c.name),
                c.gk ? el('span', { class: 'gk-tag', title: GK_TAG_TITLE }, 'GK') : null),
              el('span', { class: 'res-count', title: c.run ? `${classCountText(c)}. ${RUN_COUNT_NOTE}` : 'Lastede punkter' },
                el('span', { class: 'res-num mono' }, mill(c.count)),
                c.run ? el('span', { class: 'res-run mono' }, `fil ${signed(c.run)}`) : null));
          }));
      }));
  };

  /** Kundevisningen: «Må følges opp: N» og «Til orientering», én rad per funntype, og en kort tegnforklaring. */
  const renderCustomer = (model: ReturnType<typeof customerModel>, byId: Map<string, VectorLayer>) => {
    const groups: HTMLElement[] = [];
    for (const sev of ['oppfolging', 'orientering'] as const) {
      const rows = model.rows.filter((r) => r.severity === sev);
      if (!rows.length) continue;
      const n = sev === 'oppfolging' ? rows.reduce((s, r) => s + r.count, 0) : 0;
      groups.push(el('div', { class: `res-group res-group-${sev === 'oppfolging' ? 'avvik' : 'anlegg'}`, role: 'group', 'aria-label': sev === 'oppfolging' ? 'Må følges opp' : 'Til orientering' },
        el('h3', { class: 'res-group-title' }, sev === 'oppfolging' ? `Må følges opp: ${n.toLocaleString('nb-NO')}` : 'Til orientering'),
        ...rows.flatMap((r) => customerRow(r, byId))));
    }
    groupsEl.replaceChildren(...groups,
      groups.length ? el('p', { class: 'res-legend-note' },
        el('span', {}, el('i', { class: 'dot', style: 'background:#ef4444' }), 'Rødt i kartet: må følges opp'),
        el('span', {}, el('i', { class: 'dot', style: 'background:#cbd5e1' }), 'Grått: til orientering')) : el('p', { class: 'empty' }, 'Prosjektet har ingen analyselag ennå.'));
  };

  const customerRow = (r: CustomerRow, byId: Map<string, VectorLayer>): HTMLElement[] => {
    const ls = r.layers.map((id) => byId.get(id)).filter((l): l is VectorLayer => !!l);
    const busy = ls.some((l) => l.status === 'loading' || l.status === 'idle');
    const failed = ls.length > 0 && ls.every((l) => ['error', 'unavailable', 'network_error', 'access_error'].includes(l.status));
    const zero = !busy && r.severity === 'oppfolging' && r.count === 0;
    const box = el('input', { type: 'checkbox', checked: ls.some((l) => l.visible), 'aria-label': `Vis ${r.title}`,
      onchange: () => { for (const l of ls) host.setVisible(l.def.id, box.checked); } }) as HTMLInputElement;
    const note = r.note ? el('div', { class: 'res-note' }, el('p', {}, r.note)) : null;
    const out: HTMLElement[] = [el('div', { class: ['res-row', `res-${r.severity === 'oppfolging' ? 'avvik' : 'anlegg'}`, zero ? 'is-zero' : '', failed ? 'is-error' : ''].filter(Boolean).join(' '), 'data-funn': r.id },
      el('label', { class: 'res-toggle' }, box, el('i', { class: 'dot', style: `background:${r.color}` }),
        el('span', { class: 'res-title' }, el('span', {}, r.title), r.meta ? el('small', { class: 'res-meta' }, r.meta) : null)),
      el('span', { class: 'res-num mono' }, failed ? el('span', { class: 'err' }, 'feil') : busy && !r.value ? skeleton() : r.value || '–'),
      note ? infoToggle(r.title, note) : el('span', { class: 'res-info-spacer' }))];
    if (note) out.push(note);
    return out;
  };

  /** Én funnrad: bryter, fargeprikk, tittel (+ meta), antall, forklaring. */
  const funnRow = (def: FunnDef, byId: Map<string, VectorLayer>): HTMLElement[] => {
    const ids = funnLayers(def, new Set(byId.keys()));
    const ls = ids.map((id) => byId.get(id)!);
    const first = ls[0];
    const busy = ls.some((l) => l.status === 'loading' || l.status === 'idle');
    const failed = ls.every((l) => ['error', 'unavailable', 'network_error', 'access_error'].includes(l.status));
    const text = first.status === 'ready' && first.parsed ? funnCountText(def, first.def.id, first.parsed.features) : null;
    const { count, meta } = text ? splitCount(text) : { count: '', meta: '' };
    const zero = text !== null && /^0$/.test(count);
    const box = el('input', { type: 'checkbox', checked: ls.some((l) => l.visible), 'aria-label': `Vis ${def.title}`,
      onchange: () => { for (const l of ls) host.setVisible(l.def.id, box.checked); } }) as HTMLInputElement;
    const swatch = def.swatch ? layerClassColor(def.swatch) : undefined;
    const noteParts: HTMLElement[] = [];
    if (def.legend === 'voltage') noteParts.push(el('div', { class: 'res-legend' }, ...VOLTAGE_LEVELS.map((name, i) => el('span', {}, el('i', { class: 'dot', style: `background:${VOLTAGE_COLORS[i]}` }), name))));
    if (def.legend === 'fall' && (ids.includes(FALL_LAYER) || ids.includes('veg_fall_top_arcs'))) noteParts.push(el('p', {}, FALL_FUNN_LEGEND));
    if (def.note) noteParts.push(el('p', {}, def.note));
    const note = noteParts.length ? el('div', { class: 'res-note' }, ...noteParts) : null;
    const cls = ['res-row', `res-${def.group}`, zero ? 'is-zero' : '', failed ? 'is-error' : ''].filter(Boolean).join(' ');
    const rows: HTMLElement[] = [el('div', { class: cls, 'data-funn': def.id },
      el('label', { class: 'res-toggle' }, box,
        swatch ? el('i', { class: 'dot', style: `background:${swatch}`, title: 'Samme farge som klassen i punktskyen' }) : el('i', { class: 'dot dot-none' }),
        el('span', { class: 'res-title' }, el('span', {}, def.title), meta ? el('small', { class: 'res-meta' }, meta) : null)),
      el('span', { class: 'res-num mono' }, text !== null ? count : busy ? skeleton() : failed ? el('span', { class: 'err', title: first.message ?? 'Laget kunne ikke leses' }, 'feil') : '–'),
      note ? infoToggle(def.title, note) : el('span', { class: 'res-info-spacer' }))];
    if (note) rows.push(note);
    if (def.id === 'supports' && host.setShowUncertainSupports) {
      const ub = el('input', { type: 'checkbox', checked: !!host.showUncertainSupports?.(), 'aria-label': UNCERTAIN_SUPPORTS_TOGGLE,
        onchange: () => host.setShowUncertainSupports!(ub.checked) }) as HTMLInputElement;
      rows.push(el('label', { class: 'res-sub' }, ub, el('span', {}, UNCERTAIN_SUPPORTS_TOGGLE)));
    }
    return rows;
  };

  return {
    root, update, step,
    reset: () => { index = -1; key = ''; update(); },
    customer: () => audience === 'kunde',
    current: () => (index >= 0 && findings[index] ? { index, total: findings.length, kind: findings[index].kind, layerId: findings[index].layerId, feature: findings[index].feature } : null),
  };
}

/**
 * Kompakt infokort for valgt objekt i Resultat-visningen. Full objektinfo legges under «Alle detaljer».
 * Med `profil` (kundevisningen): én setning i klartekst og noen få rader; de tekniske radene ligger bak «Detaljer».
 */
export function resultCard(p: Picked, full: HTMLElement, profil?: Leveranseprofil | null): HTMLElement {
  const { rows, note } = cardRows(p.layer.def.id, p.layer.def.title, p.feature.props);
  if (profil) {
    const c = customerCard(p.layer.def.id, p.feature.props, profil);
    return el('div', { class: 'info result-card customer-card' },
      el('p', { class: 'res-card-headline' }, c.headline),
      c.rows.length ? el('dl', { class: 'facts' }, ...c.rows.flatMap(([k, v]) => [el('dt', {}, k), el('dd', {}, v)])) : null,
      el('p', { class: 'note' }, c.note),
      el('details', { class: 'disclosure' }, el('summary', {}, 'Detaljer'),
        el('dl', { class: 'facts' }, ...rows.flatMap(([k, v]) => [el('dt', {}, k), el('dd', {}, v)])),
        note ? el('p', { class: 'note' }, note) : null, full));
  }
  return el('div', { class: 'info result-card' },
    el('dl', { class: 'facts' }, ...rows.flatMap(([k, v]) => [el('dt', {}, k), el('dd', {}, v)])),
    note ? el('p', { class: 'note' }, note) : null,
    el('details', { class: 'disclosure' }, el('summary', {}, 'Alle detaljer'), full));
}
